import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Camera, Upload, X } from "@phosphor-icons/react";
import api, { formatApiError } from "../api";
import { BrutalButton, BrutalInput } from "./Brutal";

const CATEGORIES = ["Shipping", "Supplies", "Packaging", "Marketing", "Travel", "Dispensers", "Base Ingredients", "Lab Equipment", "Other"];

/**
 * Receipt photo → OCR → review → save.
 * If `existing` is provided, the dialog opens directly in "edit mode" with that
 * expense pre-filled and skips the photo-upload step (PATCHes on save).
 */
export default function ExpenseScanDialog({ onClose, onSaved, existing }) {
  const { t } = useTranslation();
  const fileRef = useRef(null);
  const [phase, setPhase] = useState(existing ? "form" : "upload");
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    date: existing?.date?.slice(0, 10) || new Date().toISOString().slice(0, 10),
    amount: existing?.amount != null ? String(existing.amount) : "",
    vendor: existing?.vendor || "",
    category: existing?.category || "Other",
    notes: existing?.notes || "",
    receipt_image_url: existing?.receipt_image_url || null,
    ocr_raw_json: null,
  });

  const onFileChosen = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error(t("expenses.image_only"));
      return;
    }
    setBusy(true);
    setPhase("scanning");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/expenses/scan", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 60000,
      });
      const f = data.fields || {};
      setForm((prev) => ({
        ...prev,
        date: (f.date || prev.date || "").slice(0, 10) || new Date().toISOString().slice(0, 10),
        amount: f.amount != null ? String(f.amount) : prev.amount,
        vendor: f.vendor || prev.vendor,
        category: f.category || prev.category || "Other",
        notes: f.notes || prev.notes,
        receipt_image_url: data.receipt_image_url || prev.receipt_image_url,
        ocr_raw_json: data.ocr_raw || null,
      }));
      if (data.warning) {
        toast.info(data.warning);
      } else {
        toast.success(t("expenses.ocr_ok"));
      }
      setPhase("form");
    } catch (e) {
      toast.error(formatApiError(e));
      setPhase("upload");
    } finally {
      setBusy(false);
    }
  };

  const skipOcr = () => setPhase("form");

  const submit = async (e) => {
    e.preventDefault();
    const amt = Number(form.amount);
    if (!Number.isFinite(amt) || amt < 0) {
      toast.error(t("expenses.amount_required"));
      return;
    }
    if (!form.date) {
      toast.error(t("expenses.date_required"));
      return;
    }
    setBusy(true);
    try {
      const body = {
        date: form.date,
        amount: amt,
        category: form.category || "Other",
        vendor: form.vendor.trim() || null,
        notes: form.notes.trim() || null,
        receipt_image_url: form.receipt_image_url || null,
      };
      if (existing) {
        await api.patch(`/expenses/${existing.id}`, body);
        toast.success(t("expenses.updated"));
      } else {
        await api.post("/expenses", { ...body, ocr_raw_json: form.ocr_raw_json });
        toast.success(t("expenses.saved"));
      }
      onSaved();
    } catch (e2) {
      toast.error(formatApiError(e2));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white border border-zinc-200 rounded-xl shadow-xl p-6 w-full max-w-md max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="expense-dialog"
      >
        <div className="flex items-start justify-between mb-3">
          <div>
            <h2 className="text-xl font-semibold tracking-tight">{existing ? t("expenses.edit_title") : t("expenses.new_title")}</h2>
            <p className="text-sm text-zinc-600 mt-0.5">
              {phase === "upload"
                ? t("expenses.subtitle_upload")
                : phase === "scanning"
                  ? t("expenses.scanning")
                  : t("expenses.subtitle_form")}
            </p>
          </div>
          <button type="button" onClick={onClose} className="text-zinc-500 hover:text-zinc-900" aria-label="close" data-testid="expense-close">
            <X size={18} weight="bold" />
          </button>
        </div>

        {phase === "upload" && (
          <div className="space-y-3">
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              className="w-full h-32 border-2 border-dashed border-zinc-300 rounded-xl flex flex-col items-center justify-center text-zinc-600 hover:bg-zinc-50"
              data-testid="upload-receipt-trigger"
              disabled={busy}
            >
              <Camera size={28} weight="bold" />
              <span className="mt-2 text-sm font-medium">{t("expenses.tap_to_scan")}</span>
              <span className="text-[11px] text-zinc-500 mt-0.5">{t("expenses.formats")}</span>
            </button>
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              className="hidden"
              data-testid="expense-file-input"
              onChange={(e) => onFileChosen(e.target.files?.[0])}
            />
            <BrutalButton
              type="button"
              variant="secondary"
              className="w-full"
              onClick={skipOcr}
              data-testid="skip-ocr-btn"
            >
              {t("expenses.skip_ocr")}
            </BrutalButton>
          </div>
        )}

        {phase === "scanning" && (
          <div className="py-10 text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-2 border-zinc-300 border-t-zinc-900" />
            <p className="text-sm text-zinc-600 mt-3">{t("expenses.scanning_hint")}</p>
          </div>
        )}

        {phase === "form" && (
          <form onSubmit={submit} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold mb-1">{t("expenses.field_date")}</label>
                <BrutalInput
                  type="date"
                  value={form.date}
                  onChange={(e) => setForm({ ...form, date: e.target.value })}
                  data-testid="expense-date-input"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold mb-1">{t("expenses.field_amount")}</label>
                <BrutalInput
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.amount}
                  onChange={(e) => setForm({ ...form, amount: e.target.value })}
                  data-testid="expense-amount-input"
                  placeholder="0.00"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold mb-1">{t("expenses.field_vendor")}</label>
              <BrutalInput
                type="text"
                value={form.vendor}
                onChange={(e) => setForm({ ...form, vendor: e.target.value })}
                placeholder={t("expenses.field_vendor_placeholder")}
                maxLength={200}
                data-testid="expense-vendor-input"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold mb-1">{t("expenses.field_category")}</label>
              <select
                className="input-brutal w-full"
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
                data-testid="expense-category-select"
              >
                {CATEGORIES.map((c) => (
                  <option key={c} value={c}>{t(`expenses.cat_${c.toLowerCase().replace(/ /g, "_")}`)}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold mb-1">{t("expenses.field_notes")}</label>
              <textarea
                className="input-brutal h-20 resize-none"
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder={t("expenses.field_notes_placeholder")}
                maxLength={1000}
                data-testid="expense-notes-input"
              />
            </div>
            <div className="flex gap-3 pt-1">
              <BrutalButton type="button" variant="secondary" className="flex-1" onClick={onClose} disabled={busy}>
                {t("common.cancel")}
              </BrutalButton>
              <BrutalButton type="submit" className="flex-1" disabled={busy} data-testid="expense-submit">
                {busy ? t("expenses.saving") : existing ? t("expenses.update_btn") : t("expenses.save_btn")}
              </BrutalButton>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
